import { CheckCircle2, PlugZap, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { AgentExternalAuthType } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type AgentDraft,
  emptyExternalConfig,
  type ExternalConfigDraft,
} from "@/features/agents/components/AgentDraft";
import { useTestExternalConnection } from "@/features/agents/hooks/useAgents";

interface Props {
  draft: AgentDraft;
  onChange: (next: AgentDraft) => void;
  /** undefined enquanto o agente nao foi criado (modo new). */
  agentId?: string;
  /** Ha mudancas nao salvas? Teste de conexao usa o que esta SALVO. */
  isDirty?: boolean;
}

const AUTH_TYPES: AgentExternalAuthType[] = ["none", "api_key", "hmac"];

export function ExternalConnectionTab({
  draft,
  onChange,
  agentId,
  isDirty,
}: Props) {
  const { t } = useTranslation("agents");
  const cfg = draft.external_config ?? emptyExternalConfig();
  const testMut = useTestExternalConnection(agentId ?? "");

  function patch(next: Partial<ExternalConfigDraft>) {
    onChange({ ...draft, external_config: { ...cfg, ...next } });
  }

  async function handleTest() {
    try {
      const result = await testMut.mutateAsync();
      if (result.ok) {
        toast.success(
          t("external.testOk", { ms: result.latency_ms ?? 0 }),
        );
      } else {
        toast.error(result.error ?? t("external.testFailed"));
      }
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <div className="space-y-6">
      {/* Conexao HTTP do bot. */}
      <section className="space-y-4 rounded-md border p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t("external.connectionSection")}
          </h3>
          <HealthBadge
            status={cfg.health_status}
            checkedAt={cfg.last_health_check_at}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {t("external.connectionHint")}
        </p>

        <div className="space-y-2">
          <Label htmlFor="ext-base-url">{t("external.baseUrlLabel")}</Label>
          <Input
            id="ext-base-url"
            value={cfg.base_url}
            placeholder="https://carol.exemplo.com"
            onChange={(e) => patch({ base_url: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            {t("external.baseUrlHint")}
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>{t("external.authTypeLabel")}</Label>
            <Select
              value={cfg.auth_type}
              onValueChange={(v) =>
                patch({ auth_type: v as AgentExternalAuthType })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUTH_TYPES.map((a) => (
                  <SelectItem key={a} value={a}>
                    {t(`external.authTypes.${a}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ext-timeout">
              {t("external.timeoutLabel")}
            </Label>
            <Input
              id="ext-timeout"
              type="number"
              min={5}
              max={600}
              value={cfg.timeout_seconds}
              onChange={(e) =>
                patch({ timeout_seconds: Number(e.target.value) || 120 })
              }
            />
          </div>
        </div>

        {cfg.auth_type !== "none" ? (
          <SecretField
            id="ext-secret"
            label={t("external.secretLabel")}
            hint={
              cfg.auth_type === "hmac"
                ? t("external.secretHintHmac")
                : t("external.secretHintApiKey")
            }
            value={cfg.secret}
            hasSaved={cfg.has_secret}
            onChange={(secret) => patch({ secret, clear_secret: false })}
          />
        ) : null}
      </section>

      {/* Projeto Langfuse do bot (integracao de custos). */}
      <section className="space-y-4 rounded-md border p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t("external.langfuseSection")}
        </h3>
        <p className="text-xs text-muted-foreground">
          {t("external.langfuseHint")}
        </p>

        <div className="space-y-2">
          <Label htmlFor="ext-lf-host">
            {t("external.langfuseHostLabel")}
          </Label>
          <Input
            id="ext-lf-host"
            value={cfg.langfuse_host}
            placeholder="https://langfuse.exemplo.com"
            onChange={(e) => patch({ langfuse_host: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ext-lf-pk">
            {t("external.langfusePublicKeyLabel")}
          </Label>
          <Input
            id="ext-lf-pk"
            value={cfg.langfuse_public_key}
            placeholder="pk-lf-..."
            onChange={(e) => patch({ langfuse_public_key: e.target.value })}
          />
        </div>
        <SecretField
          id="ext-lf-sk"
          label={t("external.langfuseSecretLabel")}
          hint={t("external.langfuseSecretHint")}
          value={cfg.langfuse_secret}
          hasSaved={cfg.has_langfuse_secret}
          onChange={(langfuse_secret) =>
            patch({ langfuse_secret, clear_langfuse_secret: false })
          }
        />
      </section>

      {/* Teste de conexao (usa a config SALVA no backend). */}
      <section className="flex items-center gap-3 rounded-md border border-dashed p-4">
        <Button
          type="button"
          variant="outline"
          onClick={handleTest}
          disabled={!agentId || testMut.isPending}
        >
          <PlugZap className="mr-2 h-4 w-4" />
          {testMut.isPending
            ? t("external.testing")
            : t("external.testConnection")}
        </Button>
        <p className="text-xs text-muted-foreground">
          {!agentId
            ? t("external.testNeedsSave")
            : isDirty
              ? t("external.testUsesSaved")
              : t("external.testHint")}
        </p>
      </section>
    </div>
  );
}

function SecretField({
  id,
  label,
  hint,
  value,
  hasSaved,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  hasSaved: boolean;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation("agents");
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="password"
        autoComplete="new-password"
        value={value}
        placeholder={
          hasSaved
            ? t("external.secretSavedPlaceholder")
            : t("external.secretEmptyPlaceholder")
        }
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function HealthBadge({
  status,
  checkedAt,
}: {
  status: "ok" | "error" | null;
  checkedAt: string | null;
}) {
  const { t } = useTranslation("agents");
  if (!status) return null;
  const when = checkedAt
    ? new Date(checkedAt).toLocaleString("pt-BR")
    : null;
  return (
    <Badge
      variant={status === "ok" ? "secondary" : "destructive"}
      className="gap-1"
      title={when ? t("external.lastCheckedAt", { when }) : undefined}
    >
      {status === "ok" ? (
        <CheckCircle2 className="h-3 w-3" />
      ) : (
        <XCircle className="h-3 w-3" />
      )}
      {t(`external.health.${status}`)}
    </Badge>
  );
}
